import { BarChart3, FileText, Mail, MessageSquareReply } from "lucide-react";
import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { api } from "@/api";
import { ThemeStats } from "@/components/theme/ThemeStats";

const METRICS = [
  { key: "quotes_sent", label: "Devis envoyés", color: "var(--text)" },
  { key: "automation_emails", label: "E-mails automatisés", color: "#2563eb" },
  { key: "inbox_replies", label: "Réponses envoyées", color: "#059669" },
  { key: "refusals", label: "Refus reçus", color: "#dc2626" },
];

const formatPeriod = (value, granularity) => {
  const date = new Date(`${value}T00:00:00`);
  const label = date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  return granularity === "week" ? `Sem. ${label}` : label;
};

export default function StatsPage() {
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState("day");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats/activity", { params: { days, granularity } }).then(({ data: payload }) => setData(payload)).catch(() => setData({ series: [], totals: {} }));
  }, [days, granularity]);

  return (
    <section className="page" data-testid="stats-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">STATISTIQUES</p>
          <h1>Rapports d'activité</h1>
          <p className="page-subtitle">Bilan des envois automatiques : devis envoyés, e-mails et relances partis, réponses et refus reçus sur la période choisie. Pour les appels et le travail de chaque agent, ouvrez « Production des agents ».</p>
        </div>
        {data && <ThemeStats stats={METRICS.slice(0, 3).map((m, i) => ({ icon: [FileText, Mail, MessageSquareReply][i], value: data.totals?.[m.key] ?? 0, label: `${m.label} · ${days} jours`, testid: `stats-head-${m.key}` }))} />}
      <div className="crm-seg-row">
        <div className="seg" role="group" aria-label="Période">
          {[7, 30, 90].map((value) => <button key={value} type="button" className="seg__btn" aria-pressed={days === value} onClick={() => setDays(value)} data-testid={`stats-days-${value}-button`}>{value} jours</button>)}
        </div>
        <div className="seg" role="group" aria-label="Regroupement">
          <button type="button" className="seg__btn" aria-pressed={granularity === "day"} onClick={() => setGranularity("day")} data-testid="stats-granularity-day-button">Par jour</button>
          <button type="button" className="seg__btn" aria-pressed={granularity === "week"} onClick={() => setGranularity("week")} data-testid="stats-granularity-week-button">Par semaine</button>
        </div>
      </div>
      </header>

      {!data ? (
        <p style={{ marginTop: 32 }}>Chargement des statistiques…</p>
      ) : (
        <>
          <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }} data-testid="stats-totals">
            {METRICS.map(({ key, label, color }) => (
              <div className="stat-box" key={key} data-testid={`stats-total-${key}`}>
                <span>{label}</span>
                <strong style={{ color }}>{data.totals?.[key] ?? 0}</strong>
              </div>
            ))}
          </div>

          <section className="settings-section" data-testid="stats-chart-panel">
            <div className="section-title">
              <BarChart3 size={20} />
              <div><p className="eyebrow">ÉVOLUTION</p><h2>{granularity === "week" ? "Volumes hebdomadaires" : "Volumes quotidiens"}</h2></div>
            </div>
            <div style={{ padding: "24px 16px", height: 380 }}>
              {data.series.length === 0 ? (
                <p style={{ padding: 12 }} data-testid="stats-empty-message">Aucune activité sur la période sélectionnée.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="period" tickFormatter={(value) => formatPeriod(value, granularity)} tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip labelFormatter={(value) => formatPeriod(value, granularity)} formatter={(value, name) => [value, METRICS.find((metric) => metric.key === name)?.label || name]} />
                    <Legend formatter={(value) => METRICS.find((metric) => metric.key === value)?.label || value} />
                    {METRICS.map(({ key, color }) => (
                      <Bar key={key} dataKey={key} fill={color} radius={[2, 2, 0, 0]} maxBarSize={26} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>
        </>
      )}
    </section>
  );
}
